import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import DailyIframe from "@daily-co/daily-js";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Volume2, VolumeX } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Global overlay shown to EVERY logged-in user while the admin's live mic is on.
 *
 * - Opens automatically (SSE `admin_live_audio` event or status check on load).
 * - Plays the admin's voice via Daily.co (listener-only token).
 * - Mystical logo animates to the rhythm of the audio (WebAudio analyser).
 * - User can mute/unmute locally. Closes automatically when admin stops.
 */
export const AdminLiveAudioOverlay = () => {
  const { user, token, isAdmin } = useAuth();
  const { pathname } = useLocation();
  const [active, setActive] = useState(false);
  const [muted, setMuted] = useState(false);
  const [needsTap, setNeedsTap] = useState(false);
  const [level, setLevel] = useState(0);

  const callRef = useRef(null);
  const audioRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);
  const joiningRef = useRef(false);

  const leave = useCallback(() => {
    try { cancelAnimationFrame(rafRef.current); } catch { /* noop */ }
    try { audioCtxRef.current?.close(); } catch { /* noop */ }
    audioCtxRef.current = null;
    try {
      callRef.current?.leave?.();
      callRef.current?.destroy?.();
    } catch { /* noop */ }
    callRef.current = null;
    if (audioRef.current) audioRef.current.srcObject = null;
    setActive(false);
    setLevel(0);
    setNeedsTap(false);
  }, []);

  const attachTrack = useCallback((track) => {
    const stream = new MediaStream([track]);
    if (audioRef.current) {
      audioRef.current.srcObject = stream;
      audioRef.current.muted = false;
      audioRef.current.volume = 1.0;
      // Some browsers (especially iOS Safari) need an explicit load() after
      // setting srcObject before play() will actually emit sound.
      try { audioRef.current.load?.(); } catch { /* noop */ }
      const playPromise = audioRef.current.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise.catch(() => setNeedsTap(true));
      }
      // iOS Safari may resolve play() yet keep the element silent if the
      // autoplay heuristic blocks it. Show the "Toca para escuchar" CTA as
      // a safety net — it will simply disappear the first time the user
      // confirms playback worked.
      if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
        setTimeout(() => {
          const a = audioRef.current;
          if (a && (a.paused || a.muted || a.readyState < 2)) {
            setNeedsTap(true);
          }
        }, 600);
      }
    }
    // Analyser drives the logo's "moving to the voice" animation
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const loop = () => {
        analyser.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i += 1) sum += data[i];
        setLevel(sum / data.length / 255);
        rafRef.current = requestAnimationFrame(loop);
      };
      loop();
    } catch { /* analyser optional */ }
  }, []);

  const join = useCallback(async () => {
    if (joiningRef.current || callRef.current || !token) return;
    joiningRef.current = true;
    try {
      // Make sure no stale Daily call instance is hanging around.
      try {
        const existing = DailyIframe.getCallInstance?.();
        if (existing) {
          try { existing.leave?.(); } catch { /* noop */ }
          try { existing.destroy?.(); } catch { /* noop */ }
        }
      } catch { /* noop */ }

      const { data } = await axios.post(
        `${API}/live-audio/token`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const call = DailyIframe.createCallObject({
        // Listener-only: never open the mic. Without this, Daily will ask
        // for mic permission on iOS/Android and the join will fail silently
        // if the user denies — meaning they will never hear the admin.
        audioSource: false,
        videoSource: false,
        subscribeToTracksAutomatically: true,
      });
      callRef.current = call;
      call.on("track-started", (ev) => {
        if (ev?.track?.kind === "audio" && !ev?.participant?.local) attachTrack(ev.track);
      });
      // Some Daily SDK versions only fire `track-started` when the track is
      // ADDED — if the admin was already broadcasting before we joined, we
      // need to also inspect existing participants and grab their tracks.
      call.on("participant-joined", (ev) => {
        const tracks = ev?.participant?.tracks || {};
        const at = tracks.audio?.persistentTrack || tracks.audio?.track;
        if (at && tracks.audio?.state === "playable" && !ev.participant.local) {
          attachTrack(at);
        }
      });
      await call.join({
        url: data.room_url,
        token: data.token,
        startVideoOff: true,
        startAudioOff: true,
      });
      // After join, check existing participants for an already-playing admin track.
      try {
        const parts = call.participants?.() || {};
        Object.values(parts).forEach((p) => {
          if (p.local) return;
          const t = p.tracks?.audio?.persistentTrack || p.tracks?.audio?.track;
          if (t && p.tracks?.audio?.state === "playable") attachTrack(t);
        });
      } catch { /* noop */ }
      setActive(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[live-audio] join failed:", e);
      leave();
    } finally {
      joiningRef.current = false;
    }
  }, [token, attachTrack, leave]);

  // Initial status check (late joiners / page reloads)
  useEffect(() => {
    if (!user || !token || isAdmin) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/live-audio/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && data?.active) join();
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token, isAdmin]);

  // Real-time start/stop
  useRealtimeEvents({
    admin_live_audio: (d) => {
      if (isAdmin) return;
      if (d?.active) join();
      else leave();
    },
  });

  // Cleanup on unmount
  useEffect(() => () => leave(), [leave]);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    if (audioRef.current) audioRef.current.muted = next;
  };

  const tapToListen = () => {
    const a = audioRef.current;
    if (!a) return;
    a.muted = false;
    a.volume = 1.0;
    a.play().then(() => setNeedsTap(false)).catch(() => { /* still blocked */ });
  };

  if (!user || isAdmin || pathname.startsWith("/admin")) {
    // Offscreen — NOT display:none — so iOS Safari still keeps the element
    // alive and playable. display:none can pause srcObject playback on some
    // mobile browsers.
    return (
      <audio
        ref={audioRef}
        autoPlay
        style={{ position: "absolute", left: "-9999px", width: 0, height: 0, opacity: 0 }}
      />
    );
  }

  const scale = 1 + Math.min(level * 0.9, 0.45);

  return (
    <>
      <audio
        ref={audioRef}
        autoPlay
        style={{ position: "absolute", left: "-9999px", width: 0, height: 0, opacity: 0 }}
      />
      <AnimatePresence>
        {active && (
          <motion.div
            key="live-audio-modal"
            initial={{ opacity: 0, y: 40, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.9 }}
            transition={{ type: "spring", damping: 22, stiffness: 260 }}
            className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[125] notranslate"
            translate="no"
            data-testid="admin-live-audio-modal"
          >
            <div className="relative flex items-center gap-4 bg-gradient-to-br from-[#14091e]/95 to-black/95 border border-[#d4af37]/50 rounded-2xl px-5 py-4 shadow-[0_0_60px_rgba(212,175,55,0.25)] backdrop-blur-xl">
              {/* Animated mystical logo */}
              <div className="relative w-14 h-14 flex items-center justify-center">
                <motion.div
                  animate={{ scale }}
                  transition={{ duration: 0.08, ease: "linear" }}
                  className="w-12 h-12 rounded-full bg-gradient-to-br from-[#d4af37]/40 via-purple-700/40 to-black border border-[#d4af37]/70 flex items-center justify-center"
                >
                  <Sparkles className="w-6 h-6 text-[#d4af37]" />
                </motion.div>
                <span
                  className="absolute inset-0 rounded-full border border-[#d4af37]/40"
                  style={{
                    transform: `scale(${1.1 + level * 0.8})`,
                    opacity: Math.max(0.15, 0.6 - level),
                    transition: "transform 0.08s linear",
                  }}
                />
                <span className="absolute inset-0 rounded-full border border-purple-500/30 animate-ping" />
              </div>

              <div className="min-w-[150px]">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-red-400 font-bold">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" /> En vivo
                </div>
                <div className="font-serif text-white text-base leading-tight mt-0.5">
                  Crisangel está hablando en vivo
                </div>
                {needsTap && (
                  <button
                    data-testid="live-audio-tap-listen"
                    onClick={tapToListen}
                    className="mt-1.5 text-[11px] uppercase tracking-widest text-black bg-[#d4af37] hover:bg-[#e6c350] rounded-full px-3 py-1 font-semibold"
                  >
                    🔊 Toca para escuchar
                  </button>
                )}
              </div>

              <button
                data-testid="live-audio-mute-toggle"
                onClick={toggleMute}
                title={muted ? "Activar sonido" : "Silenciar"}
                className={`w-11 h-11 rounded-full border flex items-center justify-center transition-colors ${
                  muted
                    ? "border-red-500/50 text-red-300 bg-red-500/10"
                    : "border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37]/10"
                }`}
              >
                {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
